import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, BrainCircuit, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '../../api/apiClient';

const PHASES = ['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'];

const severityColor = (severity) => {
  const s = String(severity || '').toLowerCase();
  if (s.includes('haute') || s.includes('high') || s.includes('critique')) return 'bg-red-100 text-red-700 border-red-200';
  if (s.includes('moyenne') || s.includes('medium')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

/**
 * ModeComparisonPanel - Compares manually entered modes vs Assistant ACG generated modes
 * Shows phase coverage per group, action overlap, and an optional general
 * AI security analysis of the combined mode set (persisted on the scenario).
 */
const ModeComparisonPanel = ({
  manualModes = [],
  aiModes = [],
  operationalSteps = [],
  elementaryActions = [],
  analysisId,
  attackPath = null,
  businessContext = null,
  aiEnabled = false,
  savedCritique = null,
  onSaveCritique = null,
}) => {
  const { t } = useTranslation();
  const [isComparing, setIsComparing] = useState(false);
  const [critique, setCritique] = useState(null);
  const [critiqueError, setCritiqueError] = useState(null);

  const stats = useMemo(() => {
    // Index every step under all its id forms — manual paths may reference id/_id,
    // AI paths reference elementaryActionId (same matching as the flow visualization)
    const stepsById = new Map();
    [...operationalSteps, ...elementaryActions].forEach((s) => {
      [s.elementaryActionId, s.id, s._id].filter(Boolean).forEach((id) => {
        const key = String(id);
        if (!stepsById.has(key)) stepsById.set(key, s);
      });
    });

    const analyze = (modes) => {
      const phases = new Set();
      modes.forEach((m) =>
        (m.path || []).forEach((id) => {
          const step = stepsById.get(String(id));
          if (!step) return;
          if (step.phase) phases.add(step.phase);
        }),
      );
      return { phases };
    };

    return {
      manualPhases: analyze(manualModes).phases,
      aiPhases: analyze(aiModes).phases,
    };
  }, [manualModes, aiModes, operationalSteps, elementaryActions]);

  const runCritique = async () => {
    setIsComparing(true);
    setCritiqueError(null);
    try {
      const response = await api.post('/ai/compare-modes-operatoires', {
        analysisId,
        attackPath,
        businessContext,
        manualModes,
        aiModes,
        operationalSteps,
        elementaryActions,
      });
      if (response?.success && response?.data) {
        setCritique(response.data);
        onSaveCritique && onSaveCritique(response.data);
      } else {
        setCritiqueError(t('workshop4.modesOperatoires.compareError', "L'analyse a échoué. Veuillez réessayer."));
      }
    } catch (err) {
      console.error('[ModeComparisonPanel] AI analysis failed:', err);
      setCritiqueError(t('workshop4.modesOperatoires.compareError', "L'Assistant ACG a échoué. Vérifiez que le service AI est actif et réessayez."));
    } finally {
      setIsComparing(false);
    }
  };

  // Fresh run wins over the saved critique
  const result = critique || savedCritique;
  const canAnalyze = aiEnabled && manualModes.length > 0 && aiModes.length > 0;

  const section = (titleKey, items, renderItem) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mt-4">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          {t(titleKey)}
        </div>
        <ul className="space-y-1.5">
          {items.map((item, i) => (
            <li key={i} className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
              {renderItem ? renderItem(item) : item}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div>
      <div className="text-sm font-medium text-slate-600 mb-3">
        {t('workshop4.modesOperatoires.compareModesCount', '{{manual}} {{manualLabel}} • {{ai}} {{aiLabel}}', {
          manual: manualModes.length,
          ai: aiModes.length,
          manualLabel: t('workshop4.modesOperatoires.compareModesCount_user', { count: manualModes.length }),
          aiLabel: t('workshop4.modesOperatoires.compareModesCount_ai', { count: aiModes.length }),
        })}
      </div>

      {/* Phase coverage table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 font-semibold text-sm text-slate-700">
          {t('workshop4.modesOperatoires.comparePhasesCovered', 'Phases couvertes')}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase tracking-wide bg-slate-50">
              <th className="px-4 py-2 font-medium">Phase</th>
              <th className="px-4 py-2 font-medium text-center">
                {t('workshop4.modesOperatoires.compareUser', 'Utilisateur')}
              </th>
              <th className="px-4 py-2 font-medium text-center">
                {t('workshop4.modesOperatoires.aiBadge', 'Assistant ACG')}
              </th>
            </tr>
          </thead>
          <tbody>
            {PHASES.map((phase) => (
              <tr key={phase} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium text-slate-700">{phase}</td>
                <td className="px-4 py-2 text-center">
                  {stats.manualPhases.has(phase) ? (
                    <CheckCircle className="h-4 w-4 text-green-500 inline" />
                  ) : (
                    <XCircle className="h-4 w-4 text-slate-300 inline" />
                  )}
                </td>
                <td className="px-4 py-2 text-center">
                  {stats.aiPhases.has(phase) ? (
                    <CheckCircle className="h-4 w-4 text-green-500 inline" />
                  ) : (
                    <XCircle className="h-4 w-4 text-slate-300 inline" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* General AI analysis */}
      {canAnalyze && (
        <div className="mt-5 border-t border-slate-200 pt-4">
          <button
            onClick={runCritique}
            disabled={isComparing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-all disabled:opacity-50"
          >
            <BrainCircuit className="h-4 w-4" />
            {isComparing
              ? t('workshop4.modesOperatoires.compareAnalyzing', "Analyse en cours...")
              : result
                ? t('workshop4.modesOperatoires.compareRegenerate', "Régénérer l'analyse générale")
                : t('workshop4.modesOperatoires.compareAnalyze', "Analyse générale (Assistant ACG)")}
            {isComparing && <Loader2 className="h-4 w-4 animate-spin" />}
          </button>

          {critiqueError && (
            <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {critiqueError}
            </div>
          )}

          {result && !isComparing && (
            <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
              {result.overview && (
                <p className="text-sm text-slate-700 leading-relaxed">{result.overview}</p>
              )}
              {section('workshop4.modesOperatoires.compareStrengths', result.strengths)}
              {section('workshop4.modesOperatoires.compareRemarks', result.remarks)}
              {section('workshop4.modesOperatoires.compareAdvice', result.advice)}
              {section(
                'workshop4.modesOperatoires.compareMissingAttacks',
                result.majorMissingAttacks,
                (item) => (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${severityColor(item.severity)}`}>
                        {item.severity}
                      </span>
                      <span className="font-semibold">{item.attack}</span>
                    </div>
                    {item.why && <div className="mt-1 text-xs text-slate-500">{item.why}</div>}
                  </div>
                ),
              )}
              {section(
                'workshop4.modesOperatoires.compareForgottenVulns',
                result.majorForgottenVulnerabilities,
                (item) => (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${severityColor(item.severity)}`}>
                        {item.severity}
                      </span>
                      <span className="font-semibold">{item.vulnerability}</span>
                    </div>
                    {item.reasoning && <div className="mt-1 text-xs text-slate-500">{item.reasoning}</div>}
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModeComparisonPanel;
